import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription, take, zip } from 'rxjs';
import { JSONExportHeader, JSONExportService } from '../../../shared/_services/JSONExport.service';
import { DSTSharedService } from '../../shared/dst-shared.service';
import { CreateDisclosureService, DSTTaxYear } from '../../statement-tracker/create-disclosure/create-new/create-disclosure.service';
import { StatementTrackerService } from '../../statement-tracker/statement-tracker.service';
import { DiagnosticsService } from '../diagnostics.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { generalConfigFromGlobal, hydratedGlobalParams } from '../../../shared/workspace-element-context';
import {
  buildDisplayedColumns,
  connectMatTableSort,
  getDstColStyle,
} from '../../shared/mat-table/dst-mat-grid.util';

import {
  buildDstTaxYearItems,
  resolveDstTaxYearFromEvent,
  setDstTaxYearAriaLabel,
  syncDstTaxYearItem
} from '../../shared/utils/dst-tax-year-appkit.support';
import { getAppkitDropdownSelection } from '../../../shared/utils/appkit-dropdown.util';

@Component({
    selector: 'gtw-na-report',
    templateUrl: './na-report.component.html',
    styleUrls: [
      '../../shared/styles/dst-shared.scss',
      '../../shared/mat-table/dst-mat-table.scss',
      './na-report.component.scss',
    ],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class NaReportComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(MatSort) sort!: MatSort;

  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = [];
  data: any[] = [];
  cols: any[] = [
    {
      field: 'TAXYEAR',
      header: 'Year',
      hidden: false,
      width: '80px'
    },
    {
      field: 'LEID',
      header: 'LEID',
      hidden: false,
      width: '100px'
    },
    {
      field: 'FORMNAME',
      header: 'Form',
      hidden: false,
      width: '290px'
    },
    {
      field: 'CODESECTIONNAME',
      header: 'Code Section',
      hidden: false,
      width: '180px'
    },
    {
      field: 'BUSINESSNAME',
      header: 'Business',
      hidden: false,
      width: '220px'
    },
    {
      field: 'ENTITYNAME',
      header: 'Entity Name',
      hidden: false,
      width: '210px'
    },
    {
      field: 'IGNOREREASON',
      header: 'Reasons Ignored For',
      hidden: false,
      width: '180px'
    },
    {
      field: 'IGNOREREASONDETAILS',
      header: 'Details of Reasons Ignored',
      hidden: false,
      width: '180px'
    },
    {
      field: 'MODIFIEDBY',
      header: 'Marked N/A By',
      hidden: true,
    },
    {
      field: 'MODIFIEDON',
      header: 'Modified On',
      hidden: true,
    },
  ];
  loading: boolean = true;
  taxyearLoaded: boolean = false;
  excelExportFileName: string;

  taxYears: DSTTaxYear[] = [];
  taxYearItems: { value: number; label: string }[] = [];
  selectedTaxYearItem: any = {};
  taxYear: DSTTaxYear | undefined;

  private contextApplied = false;
  private readonly subs = new Subscription();

  constructor(private cDSerivce: CreateDisclosureService,
    private dstSharedService: DSTSharedService,
    private diagnosticsService: DiagnosticsService,
    private jsonExportService: JSONExportService,
    private trackerService: StatementTrackerService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService) {

    this.excelExportFileName = 'Tracker-NA_Diagnostics_' + this.dstSharedService.getExcelExportDateString();
    this.displayedColumns = buildDisplayedColumns(this.cols);
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngAfterViewInit(): void {
    connectMatTableSort(this.dataSource, this.sort);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  getCol(field: string): { field: string; header: string; hidden?: boolean; width?: string } {
    return this.cols.find((c) => c.field === field)!;
  }

  getColStyle(col: { width?: string }): Record<string, string> {
    return getDstColStyle(col);
  }

  private applyWorkspaceContext(): void {
    if (this.contextApplied) return;
    try {
      const gc = generalConfigFromGlobal(this.gtwGlobal);
      const gp = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
      const user = this.gtwApp.USER_SETTINGS?.user || {};
      const clientKey = String(user.client?.client_key || user.client_key || '');
      const ssoId = String(user.sso_id || '');
      const roleLevel = Number(this.gtwWorkspace.workspaceFactory?.activeScreen?.parent?.access_level || 0);
      const taxYear = String(gp.tax_year || '');
      const scenario = String(gp.scenario || '');

      if (!clientKey && !ssoId) return;

      this.contextApplied = true;
      this.dstSharedService.setClientKey(clientKey);
      this.dstSharedService.setDSTUser(ssoId, roleLevel);
      this.dstSharedService.setSystemLockUnlockStatus(false, roleLevel);
      this.dstSharedService.setTaxYearScenario(taxYear, scenario);
      this.dstSharedService.setBaseURLs(gc);
      const getTaxYears$ = this.cDSerivce.getTaxYears();
      zip(getTaxYears$).pipe(take(1)).subscribe((data) => {
        this.taxYears = data[0];
        this.setTaxYear(taxYear);
        this.loadReport();
      });
    } catch (e) {
      if (e instanceof SyntaxError) {
        console.error('JSON parse Syntax Error' + e);
      }
      this.dstSharedService.showAlert('error', 'Missing gtw input params, please contact system administrator for assistance!');
    }
  }

  onTaxYearChange(): void {
    this.taxyearLoaded = true;
    this.loadReport();
  }

  setTaxYear(taxYear: string): void {
    this.taxYear = this.taxYears.find((item: any) => item.text.toString() === taxYear);
    this.taxYearItems = buildDstTaxYearItems(this.taxYears);
    this.selectedTaxYearItem = syncDstTaxYearItem(this.taxYearItems, this.taxYear, taxYear);
    this.taxyearLoaded = true;
  }

  onTaxYearChanged(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedTaxYearItem = selected || this.selectedTaxYearItem;
    const matched = resolveDstTaxYearFromEvent(event, this.taxYears);
    if (matched) {
      this.taxYear = matched;
      this.onTaxYearChange();
    }
  }

  onTaxYearSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedTaxYearItem = selected;
    }
  }

  setTaxYearAriaLabel(item: any): string {
    return setDstTaxYearAriaLabel(item);
  }

  loadReport(): void {
    this.data = [];
    this.dataSource.data = [];
    this.loading = true;
    this.diagnosticsService.loadNAReport(this.taxYear?.text.toString()!).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.data = data;
      this.dataSource.data = data;
      connectMatTableSort(this.dataSource, this.sort);
    }, (error: HttpErrorResponse) => {
      console.error(error);
      this.data = [];
      this.dataSource.data = [];
      this.dstSharedService.showAlert('error', 'Something went wrong while loading the N/A Report grid data, please try again!');
    }).add(() => {
      this.loading = false;
    });
  }

  exportToExcel(): void {
    const headersMap: JSONExportHeader[] = [
      {
        field: 'TAXYEAR',
        header: 'Year',
      },
      {
        field: 'LEID',
        header: 'LEID',
      },
      {
        field: 'FORMNAME',
        header: 'Form',
      },
      {
        field: 'CODESECTIONNAME',
        header: 'Code Section',
      },
      {
        field: 'BUSINESSNAME',
        header: 'Business',
      },
      {
        field: 'ENTITYNAME',
        header: 'Entity Name',
      },
      {
        field: 'IGNOREREASON',
        header: 'Reasons Ignored For',
      },
      {
        field: 'IGNOREREASONDETAILS',
        header: 'Details of Reasons Ignored',
      },
      {
        field: 'MODIFIEDBY',
        header: 'Marked N/A By',
      },
      {
        field: 'MODIFIEDON',
        header: 'Modified On',
      }
    ];

    this.jsonExportService.exportToExcel(this.excelExportFileName, this.data, headersMap);
  }

}
